import {
  createListenerMiddleware,
  isAnyOf,
  type TypedStartListening,
} from '@reduxjs/toolkit';

import type { AppDispatch, RootState } from 'features/app/store/store';
import {
  connectCall,
  selectIsConnected,
} from 'features/call/call-ui-state/store';
import {
  clearCaptions,
  setCaptionRequest,
  selectAreCaptionsBlockedBySignMail,
  selectShouldRunCaptionsSession,
  startCaptionsSessionThunk,
  stopCaptionsSessionThunk,
} from 'features/call/call-captions/store';
import { setDeafStatus } from 'features/call/call-deaf/store';
import { isAnyHearingConnectedSelector } from 'features/call/call-hearing/store';
import { TeamingStatus } from 'features/teaming/teaming-base/enums';
import {
  resetTeaming,
  setTeamingStatus,
  teamingStatusSelector,
} from 'features/teaming/teaming-base/store';
import {
  setVoiceSession,
  setVoiceSessionId,
  voiceSessionIdSelector,
} from 'features/voice-session/store';

// Captions lifecycle is split on purpose:
// - callCaptionsSlice extraReducers: sync cleanup (lines, startedSessionIds)
// - this middleware: demand-driven start/stop API calls and hearing-disconnect clears
// User toggles go through toggleCaptionsThunk (not setCaptionsEnabled listeners)
// to avoid racing duplicate startCaptionsSessionThunk dispatches.
//
// Stop-captions is demand-driven only: primary VCO posts stop when no requester
// wants captions (via stopCaptionsSessionThunk). Lifecycle actions (voice-session
// finish/reset/teaming leave, resetCallCaptions) clear local state in the slice
// but never call the stop API — voice-session teardown covers call end.
//
// During remote teaming only the primary VCO sender (VI1) owns start/stop API
// calls. Peer VI intent arrives via RNS → setCaptionRequest; ownership flips
// (setTeamingStatus / resetTeaming) re-evaluate whether this client should run
// the shared session. When teaming ends, clear remoteInterpreter so a departed
// peer's demand cannot keep ASR running for the remaining VI.

const listenerMiddleware = createListenerMiddleware();

type AppStartListening = TypedStartListening<RootState, AppDispatch>;
type ListenerApi = Parameters<Parameters<AppStartListening>[0]['effect']>[1];

const startListening = listenerMiddleware.startListening as AppStartListening;

const captionsStartActions = isAnyOf(
  connectCall,
  setVoiceSessionId,
  setVoiceSession
);

const captionsOwnershipActions = isAnyOf(setTeamingStatus, resetTeaming);

// String action types — .fulfilled creator imports break when thunks are mocked.
const hearingDisconnectFulfilledTypes = [
  'callHearing/notifyParticipantDisconnected/fulfilled',
  'call-hearing/disconnectById/fulfilled',
  'call-hearing/disconnectedFromCall/fulfilled',
] as const;

const maybeStartCaptionsSession = async (api: ListenerApi, source: string) => {
  const state = api.getState();

  if (!selectShouldRunCaptionsSession(state) || !selectIsConnected(state)) {
    return;
  }

  const voiceSessionId = voiceSessionIdSelector(state);

  if (!voiceSessionId) {
    return;
  }

  await api.dispatch(startCaptionsSessionThunk({ source }));
};

const voiceSessionIdChanged = (
  action: Parameters<Parameters<AppStartListening>[0]['effect']>[0],
  api: ListenerApi
) => {
  const previousId = api.getOriginalState().voiceSession.sessionId;
  const nextId = setVoiceSession.match(action)
    ? action.payload.sessionId
    : api.getState().voiceSession.sessionId;

  return Boolean(nextId && nextId !== previousId);
};

startListening({
  matcher: captionsStartActions,
  effect: async (action, api) => {
    if (
      (setVoiceSessionId.match(action) || setVoiceSession.match(action)) &&
      !voiceSessionIdChanged(action, api)
    ) {
      return;
    }

    const source = connectCall.match(action)
      ? 'call-connected'
      : setVoiceSession.match(action)
        ? 'voice-session-ready'
        : 'voice-session-reconnect';

    await maybeStartCaptionsSession(api, source);
  },
});

// Teaming role changes can move session ownership to/from this client without
// any local caption toggle. If any requester is still on, the new owner starts;
// otherwise the previous owner stops (thunk no-ops if we are not the owner).
// Covers VI1 remove VI2, VI2 leave, and VI1 leave/handoff to VI2 — after
// teaming ends there is no peer, so remote demand must drop.
startListening({
  matcher: captionsOwnershipActions,
  effect: async (_action, api) => {
    if (teamingStatusSelector(api.getState()) === TeamingStatus.NONE) {
      api.dispatch(
        setCaptionRequest({ requester: 'remoteInterpreter', enabled: false })
      );
    }

    if (selectShouldRunCaptionsSession(api.getState())) {
      await maybeStartCaptionsSession(api, 'captions-owner-change');
      return;
    }

    await api.dispatch(
      stopCaptionsSessionThunk({ source: 'captions-owner-change' })
    );
  },
});

startListening({
  predicate: (action) =>
    hearingDisconnectFulfilledTypes.includes(
      action.type as (typeof hearingDisconnectFulfilledTypes)[number]
    ),
  effect: async (_action, api) => {
    if (!isAnyHearingConnectedSelector(api.getState())) {
      api.dispatch(clearCaptions());
    }
  },
});

// Drop deaf endpoint demand when they leave. stopCaptionsSessionThunk still
// keeps the ASR session alive if a VI requester remains.
startListening({
  actionCreator: setDeafStatus,
  effect: async (action, api) => {
    if (action.payload !== 'disconnected') {
      return;
    }

    api.dispatch(
      setCaptionRequest({ requester: 'deafEndpoint', enabled: false })
    );
    await api.dispatch(stopCaptionsSessionThunk({ source: 'deaf-disconnect' }));
  },
});

// SignMail placement and AST handoff gate captions: clear lines and stop the
// session when the block starts; restart if intent is still on when it lifts.
// Uses a predicate rather than enumerating specific actions so that any state
// transition (recordingErrorOccurred, mailBoxIsFull, _resetSignMail, etc.)
// that changes the block is caught regardless of which action caused it.
startListening({
  predicate: (_action, currentState, previousState) =>
    selectAreCaptionsBlockedBySignMail(currentState) !==
    selectAreCaptionsBlockedBySignMail(previousState),
  effect: async (_action, api) => {
    const isBlocked = selectAreCaptionsBlockedBySignMail(api.getState());

    if (isBlocked) {
      api.dispatch(clearCaptions());
      await api.dispatch(
        stopCaptionsSessionThunk({ source: 'signmail-disable' })
      );
    } else {
      await maybeStartCaptionsSession(api, 'signmail-enable');
    }
  },
});

export const callCaptionsListenerMiddleware = listenerMiddleware.middleware;
